//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { forwardRef } from "react";

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Autocomplete,
  createFilterOptions,
  TextField,
} from '@mui/material';

// === COMPONENTS ===
import { ContextDoctype } from 'pages/FileViewer/components/DocumentReview/components/DoctypeContext/DoctypeContext';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

// === API ===

//#endregion

export default forwardRef( Doctype );

const filter = createFilterOptions();
//-----------------------------------------------------------------
function Doctype( props, ref ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { content, tenant, length='100%', handleChange, handleFocus } = props;

  const theme = useTheme();

  const {
    documents,
  } = useContext( ContextDoctype );

  const options  = useMemo( getOptions, [documents, tenant] )
  
  const [ document  , setDocument  ] = useState( { numcode: content, label: content, name: '' } );
  const [ input     , setInput     ] = useState( document?.label || '' );
  const [ highlight , setHighlight ] = useState( null );
  const [ is_open   , setIsOpen    ] = useState( false );

  //#endregion
  
  //#region [STYLES]

  const _styles = {
    select_doc: {
      width: length,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    paper: {
      borderRadius : '0 0 5px 5px' ,
      borderStyle  : 'solid'       ,
      borderTop    : '0',
      background   : theme.palette.background.primary[theme.palette.mode],
      borderColor  : theme.palette.primary[theme.palette.mode],
      boxShadow    : 3, // Dropdown shadow
      '& .MuiAutocomplete-option': {
        '&:hover': {
          color      : theme.palette.primary.contrastText,
          backgroundColor : theme.palette.primary.main + getHexAlpha( 50 ),
        },
        '&[aria-selected="true"]': {
          color      : theme.palette.primary.contrastText,
          backgroundColor : theme.palette.primary.main,
        },
      },
    },
  }

  const SELECT_DOC = {
    sx                   : _styles.select_doc,
    id                   : 'doc-select',
    options              : options     ,
    value                : document    ,
    size                 : 'small'     ,
    forcePopupIcon       : false    ,
    autoHighlight        : true     ,
    selected             : props.has_focus,
    disableClearable     : true,
    open                 : is_open,
    onOpen               : () => setIsOpen(true),
    onClose              : () => setIsOpen(false),
    onChange             : onChange,
    onFocus              : onFocus,
    onKeyDown            : onKeyDown,
    onInputChange        : onInputChange,
    onHighlightChange    : onHighlightChange,
    getOptionLabel       : getOptionLabel,
    groupBy              : groupBy,
    filterOptions        : filterOptions,
    renderInput          : renderInput,
    isOptionEqualToValue : isOptionEqualToValue,
    slotProps: {
      paper: {
        sx: _styles.paper
      },
    },
    ListboxProps : {
      onMouseMove: (event) => event.preventDefault()
    },
  };

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    const doc = getDocument();
    if( doc ) setDocument( doc );
  }, [ options, content] );

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onChange( event, value ) {
    //-----------------------------------------------------------------
    if( handleChange ) handleChange( value?.numcode )
  }

  //-----------------------------------------------------------------
  function onFocus( event ) {
    //-----------------------------------------------------------------
    if( handleFocus ) handleFocus( event )
  }

  //-----------------------------------------------------------------
  function onKeyDown( event ) {
    //-----------------------------------------------------------------
    try {
      if( event.key === 'PageUp' ) {
        event.defaultMuiPrevented = true;
      }
      else if( event.key === 'PageDown' ) {
        event.defaultMuiPrevented = true;
      }
      
      const is_action_force = event.key === 'Enter' &&  event.ctrlKey;
      const is_action_enter = event.key === 'Enter' && !event.ctrlKey;
      const is_action_tab   = event.key === 'Tab'   && !event.ctrlKey;
      const is_submit       = is_action_force || is_action_enter || is_action_tab;

      submit_actions:
      if( is_submit && !is_open ) {
        event.preventDefault();
        event.stopPropagation();

        const is_filter = input !== document.label;
        const content   = is_filter ? input : document.numcode;

        handleChange( content )
      }
      else if( is_submit && is_open ) {
        event.preventDefault();
        event.stopPropagation();
        event.defaultMuiPrevented = true;

        if( !highlight ) {
          break submit_actions;
        }

        setDocument( highlight );
        setInput( highlight.label );

        handleChange( highlight.numcode )
      }
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onInputChange(event, new_value, reason) {
    //-----------------------------------------------------------------
    try {
      const is_input = reason === 'input';
      const is_reset = reason === 'reset';
      const is_event = event?.type === "click" || event?.type === "keydown";
      if (is_reset && is_event ) {
        setInput(new_value);
      }

      if( is_input ) {
        setInput(new_value);
      }

      if ( is_input && new_value !== document?.label) {
        setDocument(null);
      }
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onHighlightChange(event, option) {
    //-----------------------------------------------------------------
    setHighlight(option);
  }

  //#endregion

  //#region

  //-----------------------------------------------------------------
  function getOptions() {
    //-----------------------------------------------------------------
    try {
      let items = documents[tenant];
      if( items?.length <= 0 ) {
        items = documents[''];
      }

      return items || [];
    } catch (error) {
      console.warn(error);
    }

    return [];
  }

  //-----------------------------------------------------------------
  function getDocument() {
    //-----------------------------------------------------------------
    try {
      let code = getNumCode( content );
      if( !content?.startsWith('D') ) code = 'D' + content;
      return options?.find( x=> getNumCode( x.numcode ) === code );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function getNumCode(doctype) {
    //-----------------------------------------------------------------
    try {
      if (!doctype) return '';

      let numcode = doctype;
          if (numcode[0] === 'D') ;
      else if (numcode[0] === 'F') numcode = 'F';
      else if (numcode[0] === 'B') numcode = 'B';
      else                         numcode = 'D' + numcode;

      return numcode;
    } catch (error) {
      console.warn(error);
    }

    return doctype;
  }

  //-----------------------------------------------------------------
  function getOptionLabel(option) {
    //-----------------------------------------------------------------
    return option.label
  }

  //-----------------------------------------------------------------
  function isOptionEqualToValue(option, value) {
    //-----------------------------------------------------------------
    try {
      if (typeof value === 'string') {
        return getNumCode( option.numcode ) === getNumCode( value );
      }

      return getNumCode( option.numcode ) === getNumCode( value.numcode );
    } catch (error) {
      console.warn(error);
    }

    return false;
  }
  
  //-----------------------------------------------------------------
  function groupBy(option) {
    //-----------------------------------------------------------------
    return option.group
  }

  //-----------------------------------------------------------------
  function filterOptions(options, params) {
    //-----------------------------------------------------------------
    try {
      const filtered = filter(options, params);
      const is_empty = params.inputValue === '';

      if( is_empty ) return filtered;

      const exact_match_index = filtered.findIndex(
        (option) => {
          const is_exact_numcode = getNumCode(option.numcode ) === getNumCode( params.inputValue.toUpperCase() );
          const is_eaxct_name    = option.name.toUpperCase() === params.inputValue.toUpperCase();
          return is_exact_numcode || is_eaxct_name;
        }
      );

      // If an exact match is found, move it to the beginning of the list
      if (exact_match_index > -1) {
        const exact_match = filtered.splice(exact_match_index, 1)[0];
        filtered.unshift(exact_match);
      }

      return filtered;
    } catch (error) {
      console.warn(error);
    }

    return [];
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Autocomplete { ...SELECT_DOC } />
    );
  }

  //-----------------------------------------------------------------
  function renderInput(params) {
    //-----------------------------------------------------------------
    return (<TextField {...params} 
      inputRef={ref}
      tabIndex={0}
      variant='standard' // Use a variant with no FormControl border
      InputProps={{
        ...params.InputProps,
        height : '20px',
        border :  '0px',
        disableUnderline: true,
      }}
      sx={{
        alignItems  : 'center',
        paddingTop  : '4px',
        paddingLeft : '4px',
        border      : '0px',
        '& .MuiFormControl-root': {
          display: 'unset',
        },
        '& .MuiInputBase-root': {
          height   : '26px',
          fontSize : '15px',
        },
      }}
      onKeyDown = {onKeyDown}
    />)
  }

  //#endregion

  return display();
}